import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

export const useApi = (apiFunc, options = {}) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // If you're using an auth context, you can import and use it here
  // const { logout } = useAuth(); // Uncomment if using auth context

  const {
    redirectOnUnauthorized = true,
    loginPath = '/login',
    showToastOnError = true
  } = options;

  const handleUnauthorized = useCallback(() => {
    // If using auth context, call logout method
    // logout(); // Uncomment if using auth context

    // Otherwise, clear storage manually
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');

    if (showToastOnError) {
      toast.error('Session expired. Please login again.');
    }

    if (redirectOnUnauthorized) {
      navigate(loginPath);
    }
  }, [navigate, loginPath, redirectOnUnauthorized, showToastOnError]);

  const request = useCallback(
    async (...args) => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiFunc(...args);
        setData(response.data);
        return response.data;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        const statusCode = err.response?.status;

        setError(errorMessage);

        // Only check auth and other status codes if user is online
        if (navigator.onLine) {
          // Handle different status codes
          switch (statusCode) {
            case 401:
              handleUnauthorized();
              break;
            case 403:
              if (showToastOnError) {
                toast.error(
                  'Access forbidden. You do not have permission to perform this action.'
                );
              }
              break;
            case 500:
              if (showToastOnError) {
                toast.error('Server error. Please try again later.');
              }
              break;
            default:
              if (showToastOnError) {
                toast.error(errorMessage);
              }
          }
        }
        // If offline, just set the error without any status code checks

        throw err;
      } finally {
        setLoading(false);
      }
    },
    [apiFunc, handleUnauthorized, showToastOnError]
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    data,
    error,
    loading,
    request,
    reset
  };
};
